import { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { Box, Chip, Stack, Typography, Autocomplete, TextField } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import authFetch, { responseError } from '../utils/authFetch';
import { GOLD } from '../styles/ds';

/**
 * Customer tag chips with inline editing: pick existing tags or type a new
 * name to create one on the fly. Saves the full set on every change.
 */
const TagsEditor = ({ customerId }) => {
  const notify = useNotify();
  const [allTags, setAllTags]   = useState([]);
  const [selected, setSelected] = useState([]);

  const load = useCallback(async () => {
    const [all, mine] = await Promise.all([
      authFetch('/tags?_start=0&_end=100').then(r => r.json()).catch(() => []),
      authFetch(`/customers/${customerId}/tags`).then(r => r.json()).catch(() => []),
    ]);
    setAllTags(Array.isArray(all) ? all : []);
    setSelected(Array.isArray(mine) ? mine : []);
  }, [customerId]);

  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  const save = async (tags) => {
    setSelected(tags);
    const res = await authFetch(`/customers/${customerId}/tags`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag_ids: tags.map(t => t.id) }),
    });
    if (!res.ok) notify(await responseError(res, 'שגיאה בשמירת התגיות'), { type: 'error' });
  };

  const handleChange = async (_, value) => {
    const resolved = [];
    for (const v of value) {
      if (typeof v === 'string') {
        // typed a new tag name → create it (or reuse if it already exists)
        const name = v.trim();
        if (!name) continue;
        const existing = allTags.find(t => t.name === name);
        if (existing) { resolved.push(existing); continue; }
        const res  = await authFetch('/tags', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        const body = await res.json().catch(() => null);
        if (res.ok && body) { resolved.push(body); setAllTags(ts => [...ts, body]); }
      } else {
        resolved.push(v);
      }
    }
    await save(resolved);
  };

  return (
    <Box mb={3}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <LocalOfferIcon sx={{ color: GOLD, fontSize: 18 }} />
        <Typography fontWeight={700} fontSize={14} color="primary.main">תגיות</Typography>
      </Stack>
      <Autocomplete
        multiple freeSolo size="small"
        options={allTags}
        value={selected}
        getOptionLabel={o => typeof o === 'string' ? o : o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={handleChange}
        renderTags={(value, getTagProps) =>
          value.map((tag, index) => (
            <Chip label={tag.name} size="small" {...getTagProps({ index })}
              sx={{ backgroundColor: tag.color || GOLD, color: '#fff', fontWeight: 700 }} />
          ))
        }
        renderInput={params => (
          <TextField {...params} placeholder="הוסיפי תגית (הקלדה יוצרת חדשה)..." sx={{ maxWidth: 480 }} />
        )}
      />
    </Box>
  );
};

export default TagsEditor;
